import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const TicketCategoryPage = () => {
  const [ticketCategory, setTicketCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");

  // 🔎 Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // 📦 Fetch Ticket Categories
  const fetchTicketCategory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}Ticket/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setTicketCategory(response.data.categoryList || []);
    } catch (error) {
      console.error("Error fetching ticket categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketCategory();
  }, []);

  // 🔎 Apply filters
  const filteredCategories = useMemo(() => {
    return ticketCategory.filter((item) => {
      if (
        categoryFilter &&
        !(item.category_name || "")
          .toLowerCase()
          .includes(categoryFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        userFilter &&
        !item.assign_user
          .map((user) => user.fullname.toLowerCase())
          .join(", ")
          .includes(userFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [ticketCategory, categoryFilter, userFilter]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredCategories.length);
    setPageCount(Math.ceil(filteredCategories.length / limit));
    setPage(1);
  }, [filteredCategories, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setCategoryFilter("");
      setUserFilter("");
    };
    window.addEventListener('clearCategoryFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearCategoryFilterEvent', handleClearFilters);
    };
  }, []);

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedCategories = filteredCategories.slice(start - 1, end);

  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Ticket Category</h4>
          </div>

          {/* 📑 Pagination Controls */}
          {ticketCategory.length > 0 && (
            <div className="d-inline-block ms-3 me-3">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>

        <div className="table-responsive">
          <table className="table m-b-0">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Category Name</th>
                <th>Assigned User</th>
                <th>Action</th>
              </tr>
              {/* 🔎 Search Filters Row */}
              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search category..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search user..."
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                  />
                </th>
                <th>
                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => {
                      setCategoryFilter("");
                      setUserFilter("");
                    }}
                  >
                    Clear
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="skeleton-row">
                    <td className="text-center">
                      <div className="skeleton-box" />
                    </td>
                    <td>
                      <div className="skeleton-box" />
                    </td>
                    <td>
                      <div className="skeleton-box" />
                    </td>
                    <td>
                      <div className="skeleton-box" />
                    </td>
                  </tr>
                ))
              ) : paginatedCategories.length > 0 ? (
                paginatedCategories.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td>{item.category_name}</td>
                    <td>
                      {item.assign_user.map((user) => user.fullname).join(", ")}
                    </td>
                    <td>
                      <PermissionWrapper module="ticket_category" action="edit">
                        <Link
                          className="btn-warning btn-xs"
                          style={{ float: "left", marginRight: "10px" }}
                          to={`/Ticket/Category/edit/${item.id}`}
                          title="Edit Category"
                        >
                          <i className="ti ti-pencil"></i>
                        </Link>
                      </PermissionWrapper>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center" }}>
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TicketCategoryPage;
